module.exports = function (EventEmitter, CueEmitter, WaveformPoints, WaveformSegments, KeyboardHandler, MediaElementPlayer, Player, MarkerFactories, ViewController, ZoomController, WaveformBuilder, Utils) {
    'use strict';
    function buildUi(container) {
        return {
            player: container.querySelector('.waveform'),
            zoomview: container.querySelector('.zoom-container'),
            overview: container.querySelector('.overview-container')
        };
    }
    function Peaks() {
        EventEmitter.call(this);
        this.options = {
            zoomLevels: [
                512,
                1024,
                2048,
                4096
            ],
            dataUri: null,
            dataUriDefaultFormat: 'json',
            withCredentials: false,
            waveformData: null,
            logger: null,
            keyboard: false,
            nudgeIncrement: 1,
            segmentStartMarkerColor: '#aaaaaa',
            segmentEndMarkerColor: '#aaaaaa',
            zoomWaveformColor: 'rgba(0, 225, 128, 1)',
            overviewWaveformColor: 'rgba(0,0,0,0.2)',
            overviewHighlightColor: '#aaaaaa',
            overviewHighlightOffset: 11,
            randomizeSegmentColor: true,
            height: 200,
            segmentColor: '#ff851b',
            playheadColor: '#111111',
            playheadTextColor: '#aaaaaa',
            timeLabelPrecision: 2,
            showPlayheadTime: false,
            axisGridlineColor: '#cccccc',
            axisLabelColor: '#aaaaaa',
            template: [
                '<div class="waveform">',
                '<div class="zoom-container"></div>',
                '<div class="overview-container"></div>',
                '</div>'
            ].join(''),
            pointMarkerColor: '#39cccc',
            webAudio: null,
            zoomAdapter: 'static',
            emitCueEvents: false,
            createSegmentMarker: MarkerFactories.createSegmentMarker,
            createSegmentLabel: MarkerFactories.createSegmentLabel,
            createPointMarker: MarkerFactories.createPointMarker
        };
        this.logger = console.error.bind(console);
        return this;
    }
    Peaks.prototype = Object.create(EventEmitter.prototype);
    Peaks.init = function (opts, callback) {
        var instance = new Peaks();
        opts = opts || {};
        var err = instance._setOptions(opts);
        if (err) {
            callback(err);
            return;
        }
        var containers = null;
        if (instance.options.template) {
            instance.options.container.innerHTML = instance.options.template;
            containers = buildUi(instance.options.container);
        } else if (instance.options.containers) {
            containers = instance.options.containers;
        } else {
            callback(new TypeError('Peaks.init(): The template option must be a valid HTML string or a DOM object'));
            return;
        }
        var zoomviewContainer = containers.zoomview || containers.zoom;
        if (!Utils.isHTMLElement(zoomviewContainer) && !Utils.isHTMLElement(containers.overview)) {
            callback(new TypeError('Peaks.init(): The containers.zoomview and/or containers.overview options must be valid HTML elements'));
            return;
        }
        if (zoomviewContainer && zoomviewContainer.clientWidth <= 0) {
            callback(new TypeError('Peaks.init(): Please ensure that the zoomview container is visible and has non-zero width'));
            return;
        }
        if (containers.overview && containers.overview.clientWidth <= 0) {
            callback(new TypeError('Peaks.init(): Please ensure that the overview container is visible and has non-zero width'));
            return;
        }
        if (instance.options.keyboard) {
            instance._keyboardHandler = new KeyboardHandler(instance);
        }
        var player = instance.options.player ? instance.options.player : new MediaElementPlayer(instance, instance.options.mediaElement);
        instance.player = new Player(instance, player);
        instance.segments = new WaveformSegments(instance);
        instance.points = new WaveformPoints(instance);
        instance.zoom = new ZoomController(instance, instance.options.zoomLevels);
        instance.views = new ViewController(instance);
        var waveformBuilder = new WaveformBuilder(instance);
        waveformBuilder.init(instance.options, function (err, waveformData) {
            if (err) {
                if (callback) {
                    callback(err);
                }
                return;
            }
            instance._waveformData = waveformData;
            if (containers.overview) {
                instance.views.createOverview(containers.overview);
            }
            if (zoomviewContainer) {
                instance.views.createZoomview(zoomviewContainer);
            }
            instance._addWindowResizeHandler();
            if (instance.options.segments) {
                instance.segments.add(instance.options.segments);
            }
            if (instance.options.points) {
                instance.points.add(instance.options.points);
            }
            if (instance.options.emitCueEvents) {
                instance._cueEmitter = new CueEmitter(instance);
            }
            setTimeout(function () {
                instance.emit('peaks.ready');
            }, 0);
            if (callback) {
                callback(null, instance);
            }
        });
    };
    Peaks.prototype._setOptions = function (opts) {
        if (opts.audioElement) {
            opts.mediaElement = opts.audioElement;
            this.logger('Peaks.init(): The audioElement option is deprecated, please use mediaElement instead');
        }
        if (opts.overviewHighlightRectangleColor) {
            opts.overviewHighlightColor = opts.overviewHighlightRectangleColor;
            this.logger('Peaks.init(): The overviewHighlightRectangleColor option is deprecated, please use overviewHighlightColor instead');
        }
        if (opts.inMarkerColor) {
            opts.segmentStartMarkerColor = opts.inMarkerColor;
            this.logger('Peaks.init(): The inMarkerColor option is deprecated, please use segmentStartMarkerColor instead');
        }
        if (opts.outMarkerColor) {
            opts.segmentEndMarkerColor = opts.outMarkerColor;
            this.logger('Peaks.init(): The outMarkerColor option is deprecated, please use segmentEndMarkerColor instead');
        }
        if (!opts.player) {
            if (!opts.mediaElement) {
                return new Error('Peaks.init(): Missing mediaElement option');
            }
            if (!(opts.mediaElement instanceof HTMLMediaElement)) {
                return new TypeError('Peaks.init(): The mediaElement option should be an HTMLMediaElement');
            }
        }
        if (!opts.container && !opts.containers) {
            return new Error('Peaks.init(): Please specify either a container or containers option');
        } else if (opts.container && opts.containers) {
            return new Error('Peaks.init(): Please specify either a container or containers option, but not both');
        }
        if (opts.template && opts.containers) {
            return new Error('Peaks.init(): Please specify either a template or a containers option, but not both');
        }
        if (opts.containers) {
            opts.template = null;
        }
        if (opts.logger && !Utils.isFunction(opts.logger)) {
            return new TypeError('Peaks.init(): The logger option should be a function');
        }
        if (opts.segments && !Array.isArray(opts.segments)) {
            return new TypeError('Peaks.init(): options.segments must be an array of segment objects');
        }
        if (opts.points && !Array.isArray(opts.points)) {
            return new TypeError('Peaks.init(): options.points must be an array of point objects');
        }
        Utils.extend(this.options, opts);
        if (!Array.isArray(this.options.zoomLevels)) {
            return new TypeError('Peaks.init(): The zoomLevels option should be an array');
        } else if (this.options.zoomLevels.length === 0) {
            return new Error('Peaks.init(): The zoomLevels array must not be empty');
        } else {
            if (!Utils.isInAscendingOrder(this.options.zoomLevels)) {
                return new Error('Peaks.init(): The zoomLevels array must be sorted in ascending order');
            }
        }
        if (opts.logger) {
            this.logger = opts.logger;
        }
    };
    Peaks.prototype.setSource = function (options, callback) {
        var self = this;
        if (this.options.mediaElement && !options.mediaUrl) {
            callback(new Error('peaks.setSource(): options must contain a mediaUrl when using mediaElement'));
            return;
        }
        function reset() {
            self.removeAllListeners('player.canplay');
            self.removeAllListeners('player.error');
        }
        function playerErrorHandler(err) {
            reset();
            callback(err);
        }
        function playerCanPlayHandler() {
            reset();
            if (!options.zoomLevels) {
                options.zoomLevels = self.options.zoomLevels;
            }
            var waveformBuilder = new WaveformBuilder(self);
            waveformBuilder.init(options, function (err, waveformData) {
                if (err) {
                    callback(err);
                    return;
                }
                self._waveformData = waveformData;
                [
                    'overview',
                    'zoomview'
                ].forEach(function (viewName) {
                    var view = self.views.getView(viewName);
                    if (view) {
                        view.setWaveformData(waveformData);
                    }
                });
                self.zoom.setZoomLevels(options.zoomLevels);
                callback();
            });
        }
        self.once('player.canplay', playerCanPlayHandler);
        self.once('player.error', playerErrorHandler);
        if (this.options.mediaElement) {
            self.options.mediaElement.setAttribute('src', options.mediaUrl);
        } else {
            playerCanPlayHandler();
        }
    };
    Peaks.prototype.getWaveformData = function () {
        return this._waveformData;
    };
    Peaks.prototype._addWindowResizeHandler = function () {
        this._onResize = this._onResize.bind(this);
        window.addEventListener('resize', this._onResize);
    };
    Peaks.prototype._onResize = function () {
        this.emit('window_resize');
    };
    Peaks.prototype._removeWindowResizeHandler = function () {
        window.removeEventListener('resize', this._onResize);
    };
    Peaks.prototype.destroy = function () {
        this._removeWindowResizeHandler();
        if (this._keyboardHandler) {
            this._keyboardHandler.destroy();
        }
        if (this.views) {
            this.views.destroy();
        }
        if (this.player) {
            this.player.destroy();
        }
        if (this._cueEmitter) {
            this._cueEmitter.destroy();
        }
    };
    return Peaks;
}(require('eventemitter3'), require('./cue-emitter'), require('./waveform-points'), require('./waveform-segments'), require('./keyboard-handler'), require('./mediaelement-player'), require('./player'), require('./marker-factories'), require('./view-controller'), require('./zoom-controller'), require('./waveform-builder'), require('./utils'));